import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, Box, List, ListItem, Typography } from '@mui/material';
import { DocsHRResponse, submitMiW4FormDetails, submitW4Supplement2B } from 'core/API/documents';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useCachedQueryData } from 'core/hooks/useCachedQueryData';
import {
	DelayButton,
	FormAlert,
	FormGroupSection,
	SignAndSubmit,
	StyledAccordionSummary,
	NextAndCancel,
} from 'module/documents/components/components';
import DynamicFormComponent, {
	FieldRenderer,
	GridWrapper,
} from 'module/documents/components/formTabs/DynamicFormComponent';
import { EmployerFields } from 'module/documents/components/formTabs/EmployerFields';
import { useDocumentContext } from 'module/documents/context';
import { CommonFieldsFormIndexed, MIW4FormData, MIW4FormDataIndexed } from 'module/documents/data';
import { generateMiW4FormSubmissionData } from 'module/documents/tabs/W4.utils';
import { filterFields } from 'module/documents/utils';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { FC, useMemo } from 'react';
import { handleMichiganW4PDFGeneration } from 'shared/utils/dates/Michigan_W4_pdfGenerator';
import { validateForm } from 'module/documents/utils';
import { number } from 'yup';

const step1Fields = filterFields(CommonFieldsFormIndexed, {
	include: ['first_name', 'middle_initial', 'last_name', 'date_of_birth', 'phone'],
}).map((field) =>
	['first_name', 'middle_initial', 'last_name', 'date_of_birth', 'phone'].includes(field.value) ? { ...field, readOnly: true } : field
);

const step2Fields = filterFields(CommonFieldsFormIndexed, {
	include: ['address', 'city', 'state', 'zip_code'],
}).map((field) =>
	['address', 'city', 'state', 'zip_code'].includes(field.value) ? { ...field, readOnly: true } : field
);

const step3Fields = filterFields(CommonFieldsFormIndexed, {
	include: ['ssn', 'drivers_license_number'],
}).map((field) =>
	['ssn', 'drivers_license_number'].includes(field.value) ? { ...field, readOnly: true } : field
);

// const rawFields = filterFields(MIW4FormDataIndexed, {
// 	include: ['personal_exceptions', 'deduct_amount'],
// });

const rawFields = filterFields(MIW4FormDataIndexed, {
	include: ['personal_exceptions', 'deduct_amount'],
}).map((field) => {
	const updatedField = { ...field };

	switch (field.value) {
		case 'personal_exceptions':
			return {
				...updatedField,
				pattern: '^\\d{1,2}$',
				maxLength: 2,
				customErrorMessage: 'Personal Exception must be 1 or 2 digits',
				numericOnly: true,
			};

		case 'deduct_amount':
			return {
				...updatedField,
				pattern: '^\\d{1,8}$',
				maxLength: 8,
				customErrorMessage: 'Deduct Amount must be up to 8 digits',
				numericOnly: true,
			};

		default:
			return updatedField;
	}
});

const newFields = rawFields.map((field) => {
	if (['personal_exceptions', 'deduct_amount'].includes(field.value)) {
		return {
			...field,
			allowZero: true, // 👈 You add a custom flag
		};
	}
	return field;
});

const wageFields = filterFields(MIW4FormDataIndexed, {
	include: [
		'tax_not_expected',
		'wages_exempt',
		'wages_exempt_explanation',
		'home_residence_zone',
		'home_residence_zone_explanation',
	],
});

const ExemptionInfo = () => {
	return (
		<List>
			<ListItem>
				Personal and dependency exemptions. The number of exemptions claimed here may not exceed the
				number of exemptions you are entitled to claim on a Michigan Individual Income Tax Return
				(Form MI-1040). Dependents include qualifying children and qualifying relatives under the
				Internal Revenue Code, even if your AGI exceeds the limits to claim federal tax credits for
				them.
			</ListItem>
			<ListItem>
				- Do not claim the same exemptions more than once or tax will be under-withheld.
				Specifically, do not claim:
			</ListItem>
			<ListItem>
				- Your personal exemption if someone else will claim you as their dependent.
			</ListItem>
			<ListItem>- Your personal exemption with more than one employer at a time.</ListItem>
			<ListItem>- Your spouse’s personal exemption if they claim it with their employer.</ListItem>
			<ListItem>
				- Your dependency exemptions if someone else (for example, your spouse) is claiming them
				with their employer.
			</ListItem>
		</List>
	);
};

export const Section8AExplanation = () => (
	<Accordion>
		<StyledAccordionSummary
			expandIcon={<ExpandMoreIcon />}
			aria-controls="panel1-content"
			id="panel1-header"
		>
			<Typography variant="body1">8a instructions</Typography>
		</StyledAccordionSummary>

		<AccordionDetails>
			<List>
				<ListItem>Your employment is intermittent, temporary, or less than full time</ListItem>
				<ListItem>
					Your personal and dependency exemptions exceed your annual taxable compensation;
				</ListItem>
				<ListItem>You claimed exemption from federal withholding</ListItem>
				<ListItem>
					You did not incur a Michigan income tax liability for the previous year.
				</ListItem>
			</List>
		</AccordionDetails>
	</Accordion>
);

export const Section8BExplanation = () => (
	<Accordion>
		<StyledAccordionSummary
			expandIcon={<ExpandMoreIcon />}
			aria-controls="panel1-content"
			id="panel1-header"
		>
			<Typography variant="body1">8b instructions</Typography>
		</StyledAccordionSummary>

		<AccordionDetails>
			<Typography variant="body1">
				8b: Reasons wages might be exempt from withholding include:
			</Typography>
			<List>
				<ListItem>
					You are a nonresident spouse of military personnel stationed in Michigan.
				</ListItem>
				<ListItem>
					You are a resident of one of the following reciprocal states while working in Michigan:
					Illinois, Indiana, Kentucky, Minnesota, Ohio, or Wisconsin.
				</ListItem>
				<ListItem>
					You are a member of a Native American tribe that has a tax agreement with the State of
					Michigan and whose principal place of residence is within the designated agreement area.
				</ListItem>
				<ListItem>
					You are an enrolled member of a federallyrecognized tribe that does not have a tax
					agreement with the State of Michigan, you reside within that tribe’s Indian Country (as
					defined in 18 USC 1151), and compensation from this job will be earned within that Indian
					Country.
				</ListItem>
			</List>
		</AccordionDetails>
	</Accordion>
);

export const Section8CExplanation = () => (
	<Accordion>
		<StyledAccordionSummary
			expandIcon={<ExpandMoreIcon />}
			aria-controls="panel1-content"
			id="panel1-header"
		>
			<Typography variant="body1">8c instructions</Typography>
		</StyledAccordionSummary>

		<AccordionDetails>
			<Typography variant="body1">
				8c: For questions about Renaissance Zones, contact your local assessor’s office.
			</Typography>
		</AccordionDetails>
	</Accordion>
);
interface MIW4Props {
	employeeSignatureFile: File | null;
}
export const MIW4: FC<MIW4Props> = ({
employeeSignatureFile,
}) => {
	const { formik, selectedEmployee, submittedForms, setSubmittedForms, setActiveTabIndex  } = useDocumentContext();

	const { workplaceId, organization } = useWorkplacesContext();

	const { showToast } = useToastContext();

	// Commented out - employee-form API calls not needed
	// const hrDetails = useCachedQueryData<DocsHRResponse>('employee-form/hr');
	const hrDetails = null;

	const disableNext = useMemo(() => {
		const formStatus = validateForm(formik, MIW4FormData);
		return !formStatus.valid;
	}, [formik.values]);

	const handleMiW4FormSubmit = async () => {
		const { formSubmissionData } = generateMiW4FormSubmissionData(
			formik,
			selectedEmployee.id,
			hrDetails
		);
		formSubmissionData.tax_not_expected = Boolean(formik.values.tax_not_expected);
		formSubmissionData.wages_exempt = Boolean(formik.values.wages_exempt);
		formSubmissionData.home_residence_zone = Boolean(formik.values.home_residence_zone);
		formSubmissionData.personal_exceptions = Number(formik.values.personal_exceptions);
		const rawValue = formik.values.deduct_amount;
		if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
		const parsed = Number(rawValue);
		if (!isNaN(parsed)) {
			formSubmissionData.deduct_amount = parsed;
		}
		}
		// Submit both the W4 form and Supplement 2B
		submitMiW4FormDetails(formSubmissionData, workplaceId)
			.then((supplementResponse) => {
				showToast('success', 'Form and Supplement have been submitted successfully!');
				console.debug('response:', supplementResponse);
				setSubmittedForms(prev => ({
					...prev,
					mi_w4_form_submitted: true,
				}));
				setActiveTabIndex(9);
			})
			.catch((error) => {
				console.error('Error submitting form:', error);
				showToast('error', 'Something went wrong!');
			});



		// //----------------------Michigan Editing existing PDF and sending to API------------------
		try {

			handleMichiganW4PDFGeneration({
				formik,
				employeeSignatureFile,
				workplaceName: selectedEmployee?.name,
				organizationName: organization.name,
				employeeEmail: selectedEmployee.email,
			}); 
		
		} catch (error) {
			console.error("Error filling PDF:", error);
		}
		// //---------------Michigan Editing existing PDF and sending to API ENDS HERE --------------	
		
		//----------------------Ohio Editing existing PDF and sending to API------------------
		// try {
		// 	// Step 1: Prepare Text Fields
		// 	const textFields = {
		// 		"Employee Name": (formik.values?.first_name || ""),
		// 		"Employee SSN": formik.values?.ssn,
		// 		"Address city state ZIP code": (formik.values?.address || "") + ", " + (formik.values?.city || "") + ", " + (formik.values?.state || "") + ", " + (formik.values.zip_code || ""),
		// 		"School district of residence See The Finder at taxohiogov": formik.values?.address || "",
		// 		"School district number": "",
		// 		"undefined": "N",
		// 		"undefined_2": "N",
		// 		"undefined_3": "N/A",
		// 		"undefined_4": "N/A",
		// 		"undefined_5": "N/A",
		// 		"Date": "04/03/2025"
		// 		// "1 Full Social Security Number": formik.values?.ssn,
		// 		// "2 Date of Birth": formik.values?.date_of_birth,
		// 		// "3 Name First Middle Initial Last": (formik.values?.first_name || "") + " " + (formik.values?.middle_initial || "") + " " + (formik.values?.last_name || ""),
		// 		// "4 Drivers License Number or State ID": formik.values?.drivers_license_number || "",
		// 		// "Home Address No Street PO Box or Rural Route": formik.values?.address || "",
		// 		// "mmddyyyy": new Date(), // Example date
		// 		// "City or Town": formik.values?.city || "",
		// 		// "State": formik.values?.state || "",
		// 		// "ZIP Code": formik.values?.zip_code?.length <= 6 ? String(formik.values.zip_code) : "",
		// 		// "Date": new Date(),
		// 		// "10 Employers Name": (formik?.values?.employer_name || ""),
		// 		// "11 Federal Employer Identification Number": String(formik?.values?.employer_ein).length < 10 ? formik?.values?.employer_ein : "",
		// 		// "Address": (formik?.values?.employer_business_address || ""),
		// 		// "City": (formik?.values?.employer_city || ""),
		// 		// "State_2": (formik?.values?.employer_state || ""),
		// 		// "ZIP Code_2": (formik?.values?.employer_zip_code || ""),
		// 		// "Name of Contact Person": "Jane Doe",
		// 		// "Contact Phone Number": "555-55555",
		// 		// "Enter the number of personal and dependent exemptions": formik?.values?.personal_exceptions > 2 ? "" : formik?.values?.personal_exceptions,
		// 		// "Additional amount you want deducted from each pay": (formik?.values?.deduct_amount || ""),
		// 	};
		// 	// formik?.values?.wages_exempt && Object.assign(textFields, { "Explain Wages are exempt from withholding": formik?.values?.wages_exempt_explanation});
		// 	// formik?.values?.home_residence_zone && Object.assign(textFields, { "Permanent home domicile is located in the following Renaissance Zone": formik?.values?.home_residence_zone_explanation});
		// 	const checkboxes = []; 
		// 	// formik?.values?.tax_not_expected && checkboxes.push("A Michigan income tax liability is not expected this year");
		// 	// checkboxes.push("Waiver2");
		// 	// formik?.values?.home_residence_zone && checkboxes.push("C  Permanent home domicile is located in the following Renaissance Zone");

		// 	const radioGroupMappings = {};

		// 	// Object.assign(radioGroupMappings, {
		// 	// "5 Are you a new employee": formik?.values.new_employee ? "Yes" : "No", 
		// 	// });

		// 	// Step 4: Prepare Signatures
		// 	const signatures = [];
	
		// 	if (employeeSignatureFile || formik.values.internal_signature_file) {
		// 		signatures.push(
		// 			{
		// 				file: employeeSignatureFile || (process.env.REACT_APP_URL + formik.values.internal_signature_file),
		// 				position: {'x': 150, 'y':350, 'width': 100, 'height': 23},
		// 				pageNum: 0,
		// 			}
		// 		);
		// 	}

		// 	const textOverlays = [];
		// 	// textOverlays.push(
		// 	// 	{
		// 	// 	  text: `General Manager`, //General manager name
		// 	// 	  position: { x: 130, y: 370 },
		// 	// 	  pageNum: 0,
		// 	// 	},			
		// 	// );

		// 	// Step 5: Build filename prefix
		// 	const filePrefix = `Ohio_State_W4${formik.values?.last_name}_${formik.values?.first_name}`;
			
		// 	// Step 6: Generate the filled PDF
		// 	await generateAndDownloadPDF(
		// 		"/Ohio_State_W4.pdf",
		// 		textFields,
		// 		{},
		// 		checkboxes,
		// 		filePrefix,
		// 		workplaceId,
		// 		formik?.values.id,
		// 		signatures,
		// 		textOverlays,
		// 		radioGroupMappings
		// 	);


		// } catch (error) {
		// 	console.error("Error filling PDF:", error);
		// }
		// //---------------Ohio Editing existing PDF and sending to API ENDS HERE --------------	
	};

	return (
		<>
			<Box mb={2}>
				<Typography variant="h4" mb="24px">
					{MIW4FormData.title}
				</Typography>

				<Typography variant="body1">
					This certificate is for Michigan income tax withholding purposes only.
				</Typography>
			</Box>

			<Box display="flex" flexDirection="column" gap={2}>
				<FormGroupSection title="Enter Personal Information">
					<DynamicFormComponent>
						{step1Fields.map((field, index) => (
							<GridWrapper key={index} field={field} index={index}>
								<FieldRenderer field={field} formik={formik} />
							</GridWrapper>
						))}
					</DynamicFormComponent>
				</FormGroupSection>
				<FormGroupSection>
					<DynamicFormComponent>
						{step2Fields.map((field, index) => (
							<GridWrapper key={index} field={field} index={index}>
								<FieldRenderer field={field} formik={formik} />
							</GridWrapper>
						))}
					</DynamicFormComponent>
				</FormGroupSection>
				<FormGroupSection>
					<DynamicFormComponent>
						{step3Fields.map((field, index) => (
							<GridWrapper key={index} field={field} index={index}>
								<FieldRenderer field={field} formik={formik} />
							</GridWrapper>
						))}
					</DynamicFormComponent>
				</FormGroupSection>
				<FormGroupSection>
					<DynamicFormComponent>
						<GridWrapper field={MIW4FormDataIndexed['new_employee']} index={0}>
							<FieldRenderer field={MIW4FormDataIndexed['new_employee']} formik={formik} />
						</GridWrapper>

						{formik.values.new_employee && (
							<GridWrapper field={MIW4FormDataIndexed['first_date_employment']} index={0}>
								<FieldRenderer
									field={MIW4FormDataIndexed['first_date_employment']}
									formik={formik}
								/>
							</GridWrapper>
						)}
					</DynamicFormComponent>
				</FormGroupSection>
				<FormGroupSection title="Withholding Exemptions">
					<FormAlert severit="info">
						<ExemptionInfo />
					</FormAlert>

					<DynamicFormComponent marginTop="12px">
						<Typography variant="body1">
							I claim exemption from withholding because (see instructions):
						</Typography>
						{newFields.map((field, index) => (
							<GridWrapper key={index} field={field} index={index}>
								<FieldRenderer field={field} formik={formik} />
							</GridWrapper>
						))}
					</DynamicFormComponent>
				</FormGroupSection>
				<FormGroupSection>
					{/* <FormAlert severit="info">
						<ExemptionInfo />
					</FormAlert> */}

					<DynamicFormComponent marginTop="12px">
						<Typography variant="h6">
							I claim exemption from withholding because (see instructions):
						</Typography>
						{wageFields.map((field, index) => (
							<GridWrapper key={index} field={field} index={index}>
								<FieldRenderer field={field} formik={formik} />
							</GridWrapper>
						))}
					</DynamicFormComponent>

					<Box paddingY="12px">
						<Section8AExplanation />
					</Box>
					<Box paddingY="12px">
						<Section8BExplanation />
					</Box>
					<Box paddingY="12px">
						<Section8CExplanation />
					</Box>
				</FormGroupSection>
			</Box>
			<SignAndSubmit onSubmit={handleMiW4FormSubmit} formData={MIW4FormData} />
			{/* <FormGroupSection marginTop="24px">
				<FormGroupSection
					title="Employer Information"
					isEmployer={true}
					marginTop="24px"
					marginBottom="12px"
				>
					<EmployerFields
						fieldValues={[
							'employer_title',
							'employer_business_name',
							'employer_business_address',
							'employer_city',
							'employer_state',
							'employer_zip_code',
							'employer_ein',
						]}
						readOnlyFields={[
							'employer_title',
							'employer_business_name',
							'employer_business_address',
							'employer_city',
							'employer_state',
							'employer_zip_code',
							'employer_ein',
						]}
					/>
				</FormGroupSection>
				<FormAlert>
					Under penalties of perjury, I declare that this certificate, to the best of my knowledge
					and belief, is true, correct, and complete.
				</FormAlert>
				<Box paddingY="8px">
					<DelayButton
						variant="contained"
						onClick={() => {
							formik.setFieldValue('signature_approved', true);
						}}
						style={{ width: 'max-content', color: "white", backgroundColor: 'rgb(188, 39, 39)', }}
					>
						Manager Signature
					</DelayButton>
				</Box>
			</FormGroupSection> */}
			<NextAndCancel
				nextTabIndex={9}
				formData={MIW4FormData}
				isNextDisabled={!submittedForms.mi_w4_form_submitted}
			/>
		</>
	);
};
